import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import i18n from "i18next";
import { connect } from "react-redux";
import {
  isAppInWhiteLabelMode,
  withRouter,
  listOAuthConnections,
  getUserAPIKeys,
  userOAuthConnectionsSelector,
  userAPIKeysSelector,
  filterAPIKeysByType,
  userApiKeyTypes,
  showCopiedToast
} from "@kubera/common";
import { DialogOverlay, Dialog } from "components/dialog/DialogOverlay";
import PrimaryButton from "components/button/PrimaryButton";
import TextInput from "components/inputs/TextInput";
import {
  KUBERA_AI_SYNC_HELP_URL,
  KUBERA_WEB_SYNC_AI_ASSISTANT_HELP_URL
} from "components/ai_sync_extension/AiSyncExtensionExports";
import AccountSettingsComponentExports, {
  accountSettingsTabs
} from "components/account_settings/AccountSettingsComponentExports";
import { aiAssistants, isAIAssistantConnected } from "components/account_settings/AIAssistantsConfig";
import GreenTickIcon from "assets/images/green_tick.svg?react";
import { hashParams, modalValues } from "routes";

const CHATGPT_ASSISTANT_ID = "chatgpt";
const WEB_SYNC_AI_ASSISTANT_IDS = ["claude", CHATGPT_ASSISTANT_ID, "grok"];
const OPEN_AI_ASSISTANTS_SETTINGS_ACTION = "open-ai-assistants-settings";
const INSTITUTION_NAME_PLACEHOLDER = "%institutionName%";

const Overlay = styled(DialogOverlay)``;

const StyledDialog = styled(Dialog)`
  width: 675px;
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 80px;
  background: #fcfcfc;
  box-sizing: border-box;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 55px 66px 61px 59px;
  width: 100%;
  box-sizing: border-box;
  color: #000;
  font-feature-settings: "ss01" on;
`;

const Title = styled.div`
  font-weight: 700;
  font-size: 22px;
  line-height: 130%;
  letter-spacing: -0.66px;
`;

const Description = styled.div`
  margin-top: 2px;
  font-weight: 400;
  font-size: 14px;
  line-height: 150%;
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${props => props.topMargin || "31px"};
`;

const StepNumber = styled.div`
  font-weight: 700;
  font-size: 13px;
  line-height: 150%;
`;

const StepBody = styled.div`
  font-weight: 400;
  font-size: 12px;
  line-height: 150%;
  color: ${props => props.bodyColor};

  strong {
    display: block;
    font-weight: 700;
    font-size: 14px;
    line-height: 150%;
    color: #000;
  }

  a {
    color: #0074fc;
    font-weight: ${props => props.linkWeight};
    text-decoration: underline;
    cursor: pointer;
  }
`;

const ConnectedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 30px;
  font-size: 14px;
  line-height: 150%;
`;

const ConnectedText = styled.div`
  font-weight: 700;
`;

const ManageLink = styled.div`
  margin-left: 6px;
  font-weight: 400;
  font-size: 11px;
  line-height: 150%;
  color: rgba(0, 0, 0, 0.5);
  text-transform: uppercase;
  text-decoration: underline;
  cursor: pointer;
`;

const InstitutionInput = styled(TextInput)`
  height: 45px;
  margin-top: 12px;
  padding: 0 12px;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.5);
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  letter-spacing: -0.21px;
  color: #000;
  font-feature-settings: "ss01" on, "lnum" on, "pnum" on;
`;

const PromptBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: -1px;
  padding: 30px 20px;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const PromptText = styled.div`
  font-family: "Roboto Mono", monospace;
  font-weight: 400;
  font-size: 14px;
  line-height: 150%;
  letter-spacing: -0.14px;
  text-transform: uppercase;
  color: #000;
  word-break: break-word;
`;

const PromptInstitutionName = styled.span`
  color: #008f05;
`;

const CopyPromptButton = styled(PrimaryButton)`
  height: 44px;
  min-width: 131px;
  margin-top: 20px;
  padding: 0 15px;
  background-color: #000;
  color: #fff;
  font-family: Inter;
  font-weight: 700;
  font-size: 12px;
  line-height: 110%;
`;

const Footer = styled.div`
  margin-top: 20px;
  font-weight: 400;
  font-size: 12px;
  line-height: normal;
  color: #5c5c5c;

  a {
    color: inherit;
    text-decoration: underline;
  }
`;

const AiSyncIntroDialog = ({
  history,
  location,
  onDismiss,
  dismissOnAction,
  disableOverlayDismiss,
  oAuthConnections,
  apiKeys,
  listOAuthConnections,
  getUserAPIKeys,
  showCopiedToast
}) => {
  const [institutionName, setInstitutionName] = useState("");

  useEffect(() => {
    listOAuthConnections().catch(() => {});
    getUserAPIKeys().catch(() => {});
  }, [listOAuthConnections, getUserAPIKeys]);

  const connectedAssistants = useMemo(() => {
    const tokens = filterAPIKeysByType(apiKeys, userApiKeyTypes.MCP);
    const connections = oAuthConnections || [];
    return WEB_SYNC_AI_ASSISTANT_IDS.map(id => aiAssistants.find(assistant => assistant.id === id)).filter(assistant =>
      isAIAssistantConnected(assistant, tokens, connections)
    );
  }, [apiKeys, oAuthConnections]);

  const isConnected = connectedAssistants.length > 0;

  const desktopAppInstruction = useMemo(() => {
    const isChatgptConnected = connectedAssistants.some(assistant => assistant.id === CHATGPT_ASSISTANT_ID);
    if (connectedAssistants.length === 1) {
      return isChatgptConnected
        ? i18n.t("aiSyncIntroDialog.desktopAppChatgpt")
        : i18n.t("aiSyncIntroDialog.desktopAppNamed").replace("%name%", connectedAssistants[0].name);
    }
    if (connectedAssistants.length > 1 && isChatgptConnected === false) {
      return i18n.t("aiSyncIntroDialog.desktopAppGeneric");
    }
    return i18n.t("aiSyncIntroDialog.desktopAppGenericWithChatgpt");
  }, [connectedAssistants]);
  const promptInstitutionName = institutionName.trim() || i18n.t("aiSyncIntroDialog.institutionName");
  const [promptPrefix, promptSuffix] = i18n
    .t("aiSyncIntroDialog.prompt")
    .replace("%appName%", isAppInWhiteLabelMode() === true ? window.location.hostname : i18n.t("appName"))
    .split(INSTITUTION_NAME_PLACEHOLDER);

  const handleDismiss = useCallback(() => {
    if (onDismiss) {
      onDismiss();
    }
  }, [onDismiss]);

  const openAIAssistantsSettings = useCallback(() => {
    AccountSettingsComponentExports.show(history, location, accountSettingsTabs.AI_ASSISTANTS);
  }, [history, location]);

  const handleLinkClick = useCallback(
    e => {
      if (e.target.dataset.action === OPEN_AI_ASSISTANTS_SETTINGS_ACTION) {
        openAIAssistantsSettings();
        return;
      }
      if (dismissOnAction && e.target.closest("a")) {
        handleDismiss();
      }
    },
    [dismissOnAction, handleDismiss, openAIAssistantsSettings]
  );

  const handleCopyPrompt = useCallback(() => {
    navigator.clipboard.writeText(`${promptPrefix}${promptInstitutionName}${promptSuffix}`).then(() => {
      showCopiedToast(i18n.t("promptCopied"));
    });
  }, [promptPrefix, promptInstitutionName, promptSuffix, showCopiedToast]);

  const connectedNamesText = useMemo(
    () =>
      new Intl.ListFormat(i18n.language, { style: "long", type: "conjunction" }).format(
        connectedAssistants.map(assistant => assistant.name)
      ),
    [connectedAssistants]
  );

  return (
    <Overlay onDismiss={disableOverlayDismiss ? undefined : handleDismiss}>
      <StyledDialog>
        <Container>
          <Title>{i18n.t("aiSyncIntroDialog.title")}</Title>
          <Description>{i18n.t("aiSyncIntroDialog.description")}</Description>
          {isConnected ? (
            <ConnectedRow>
              <GreenTickIcon />
              <ConnectedText>
                {i18n.t("aiSyncIntroDialog.connectedTo").replace("%names%", connectedNamesText)}
              </ConnectedText>
              <ManageLink onClick={openAIAssistantsSettings}>{i18n.t("manage")}</ManageLink>
            </ConnectedRow>
          ) : (
            <Step>
              <StepNumber>01</StepNumber>
              <StepBody
                bodyColor="#000"
                linkWeight="400"
                onClick={handleLinkClick}
                dangerouslySetInnerHTML={{ __html: i18n.t("aiSyncIntroDialog.connectStep") }}
              />
            </Step>
          )}
          <Step topMargin={isConnected ? "30px" : undefined}>
            {isConnected === false && <StepNumber>02</StepNumber>}
            <StepBody
              bodyColor="#222"
              linkWeight="500"
              onClick={handleLinkClick}
              dangerouslySetInnerHTML={{
                __html: i18n
                  .t("aiSyncIntroDialog.promptStep")
                  .replace("%desktopAppInstruction%", desktopAppInstruction)
                  .replace("%helpUrl%", KUBERA_WEB_SYNC_AI_ASSISTANT_HELP_URL)
              }}
            />
          </Step>
          <InstitutionInput
            stretch
            autoFocus
            value={institutionName}
            placeholder={i18n.t("aiSyncIntroDialog.institutionPlaceholder")}
            onChange={e => setInstitutionName(e.target.value)}
          />
          <PromptBox>
            <PromptText>
              {promptPrefix}
              <PromptInstitutionName>{promptInstitutionName}</PromptInstitutionName>
              {promptSuffix}
            </PromptText>
            <CopyPromptButton onClick={handleCopyPrompt} title={i18n.t("aiSyncIntroDialog.copyPrompt")} />
          </PromptBox>
          <Footer
            onClick={handleLinkClick}
            dangerouslySetInnerHTML={{
              __html: i18n.t("aiSyncIntroDialog.footer").replace("%helpUrl%", KUBERA_AI_SYNC_HELP_URL)
            }}
          />
        </Container>
      </StyledDialog>
    </Overlay>
  );
};

const mapStateToProps = state => ({
  oAuthConnections: userOAuthConnectionsSelector(state),
  apiKeys: userAPIKeysSelector(state)
});

const mapDispatchToProps = {
  listOAuthConnections,
  getUserAPIKeys,
  showCopiedToast
};

const ConnectedAiSyncIntroDialog = withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(AiSyncIntroDialog)
);

ConnectedAiSyncIntroDialog.show = (history, location, shouldReplace = false) => {
  const target = {
    ...location,
    hash: `${hashParams.MODAL}=${modalValues.AI_SYNC_INTRO}`
  };
  if (shouldReplace) {
    history.replace(target);
  } else {
    history.push(target);
  }
};

export default ConnectedAiSyncIntroDialog;
